import { useCallback, useEffect, useMemo } from 'react';
import {
  Navigate,
  Route,
  Routes,
  useNavigate,
  useParams,
} from 'react-router-dom';
import Helmet from 'react-helmet';

import {
  FormResponse,
  Participant,
  useApply,
  useForm,
  usePublicSession,
  useRecruitment,
  useShortUrl,
} from '@app/api';
import { setLocale } from '@app/i18n';

import { PublicBackground, useToast } from '@app/core';

import {
  AnalyticsCategoryProvider,
  Categories,
  useFingerprint,
  useTrackApplied,
  useTrackingParams,
  useTrackScreenerFinished,
} from '@app/analytics';
import { StudyProfile, useProfileSteps } from './StudyProfile';
import { useBuildCreateFormResponse } from './CustomForm/hooks/useBuildCreateFormResponse';
import { useRenderField } from './CustomForm/hooks/useRenderField';
import { useAppliedRedirection } from './hooks/useAppliedRedirection';
import { useTranslation } from 'react-i18next';

interface Props {}

export const NoSignupUnmoderatedApplication = ({}: Props) => {
  const { id } = useParams<{ id: string }>();
  const { data: session } = usePublicSession(id);
  const { t } = useTranslation();

  const { data: recruitment } = useRecruitment(session?.recruitmentId);
  const sessionLinkId = !session || id === session.id ? undefined : id;
  const shortUrl = useShortUrl();

  const redirect = useAppliedRedirection(sessionLinkId);

  const navigate = useNavigate();
  const toast = useToast();
  const tracking = useTrackingParams();

  const criteriaRefs = useMemo(() => {
    if (!recruitment?.criteria) {
      return [];
    }
    return recruitment.criteria.map((c) => c.fieldRef);
  }, [recruitment?.criteria]);

  const startedAt = useMemo(() => new Date(), []);
  const { buildCreateFormResponse, setScreenerResponse } =
    useBuildCreateFormResponse({
      screenerFormId: recruitment?.screeningFormId,
      startedAt,
      criteria: recruitment?.criteria || [],
    });

  const { isLoading, data: form } = useForm(recruitment?.screeningFormId);

  const { mutateAsync: applyMutation } = useApply();
  const trackApplied = useTrackApplied();
  const trackScreener = useTrackScreenerFinished();
  const fingerprint = useFingerprint();
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const apply = useCallback(
    async (
      responseParam?: FormResponse,
      profileParam?: Partial<Participant>,
    ) => {
      if (!recruitment || !session) {
        return;
      }
      const formResponse = buildCreateFormResponse(responseParam, profileParam);

      const application = await applyMutation({
        recruitmentId: recruitment.id,
        sessionId: session.id,
        sessionLinkId,
        tracking,
        trackingId: tracking.tid,
        formResponse,
        sync: true,
        fingerprint,
        timezone,
      });
      trackApplied({
        sessionId: session.id,
        sessionLink: sessionLinkId,
      });
      redirect(application);
    },
    [
      applyMutation,
      navigate,
      toast,
      tracking,
      buildCreateFormResponse,
      sessionLinkId,
      shortUrl,
    ],
  );

  const onProfileFinished = useCallback(
    (profile: Partial<Participant>) => apply(undefined, profile),
    [apply],
  );

  const locale = session?.locale;
  useEffect(() => {
    setLocale(locale);
  }, [locale]);

  const {
    currentFieldsByCategories,
    steps: profileSteps,
    currentStep: currentProfileStep,
    onSubmit: onProfileSubmit,
  } = useProfileSteps({
    criteriaRefs,
    onFinish: onProfileFinished,
  });

  const onFormFinished = useCallback(
    (formResponse?: FormResponse) => {
      if (formResponse) {
        setScreenerResponse(formResponse);
        trackScreener();
      }

      if (!profileSteps.length) {
        return apply(formResponse);
      }

      navigate('profile');
      return;
    },
    [navigate, trackScreener, setScreenerResponse, profileSteps, apply],
  );

  const steps = useMemo(
    () => [...(form?.fieldIds || []), ...profileSteps],
    [form?.fieldIds, profileSteps],
  );
  const pageCount = steps.length;

  const [renderField, pagination] = useRenderField({
    onFinishForm: onFormFinished,
    form,
  });

  if (!session || !recruitment) {
    return null;
  }

  return (
    <AnalyticsCategoryProvider category={Categories.SURVEY}>
      <Helmet>
        <title>{session.title} - Tandemz</title>
        <meta property="og:title" content={session.title} />
        <meta property="og:description" content={session.description} />
        <meta name="description" content={session.description} />
      </Helmet>
      <Routes>
        <Route
          index
          element={
            <PublicBackground
              isLoading={isLoading}
              title={t('participant.publicPageTitle.applyToStudies')}
              progression={pagination.page}
              pageCount={pageCount || pagination.length}
              formContent
              noHeader
            >
              {form ? renderField() : <Navigate to="profile" />}
            </PublicBackground>
          }
        />

        <Route
          path="profile"
          element={
            <PublicBackground
              isLoading={isLoading || !currentFieldsByCategories}
              title={t('participant.publicPageTitle.applyToStudies')}
              progression={steps.indexOf(currentProfileStep)}
              pageCount={pageCount}
              formContent
              noHeader
            >
              {currentFieldsByCategories && (
                <StudyProfile
                  criteriaRefs={criteriaRefs}
                  recruitment={recruitment}
                  onSubmit={onProfileSubmit}
                  currentFieldsByCategories={currentFieldsByCategories}
                />
              )}
            </PublicBackground>
          }
        />
      </Routes>
    </AnalyticsCategoryProvider>
  );
};
